import {
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    Divider,
    InlineStack,
    Layout,
    Select,
    SkeletonBodyText,
    Text,
    TextField,
} from "@shopify/polaris";
import { SafeSaveBar as SaveBar } from "../../app/components/SafeAppBridge";
import { DeleteIcon, DragHandleIcon, PlusIcon } from "@shopify/polaris-icons";
import {
    type PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import agent from "../../app/api/agent";
import type {
    FitmentCatalogSettings,
    FitmentFieldDataType,
    FitmentFieldDefinition,
    FitmentFieldSortDirection,
    FitmentFieldSortMode,
} from "../../app/models/FitmentCatalog";
import { setCachedFitmentCatalogLabel } from "../../app/utils/fitmentCatalogLabel";
import { SettingsHelpTooltip, SettingsTooltip } from "./SettingsTooltip";

const dataTypeOptions = [
    { label: "Text", value: "Text" },
    { label: "Whole number", value: "Number" },
    { label: "Decimal", value: "Decimal" },
    { label: "Date", value: "Date" },
];

const sortDirectionOptions = [
    { label: "Ascending", description: "Sort from lowest to highest, or A to Z.", value: "Asc" },
    { label: "Descending", description: "Sort from highest to lowest, or Z to A.", value: "Desc" },
];

const sortModeOptions = [
    { label: "Natural numeric", description: "Recognize numbers within text, so Model 2 sorts before Model 10.", value: "NaturalNumeric" },
    { label: "Alphabetical", description: "Compare values as plain text, so Model 10 sorts before Model 2.", value: "Alphabetical" },
];

const fieldRoleOptionGroups = [
    {
        title: "General",
        options: [
            { label: "Custom", value: "custom" },
        ],
    },
    {
        title: "Standard fitment fields",
        options: [
            { label: "Year", value: "year" },
            { label: "Make", value: "make" },
            { label: "Model", value: "model" },
            { label: "Submodel / Trim", value: "submodel" },
            { label: "Engine", value: "engine" },
            { label: "Displacement", value: "displacement" },
            { label: "Body / Vehicle Type", value: "body_vehicle_type" },
            { label: "Drive Type", value: "drive_type" },
            { label: "Transmission", value: "transmission" },
            { label: "Fuel Type", value: "fuel_type" },
            { label: "Cab Type", value: "cab_type" },
            { label: "Bed Length", value: "bed_length" },
            { label: "Wheelbase", value: "wheelbase" },
            { label: "Doors", value: "doors" },
            { label: "Brake Type", value: "brake_type" },
            { label: "Brake Size", value: "brake_size" },
            { label: "Tire Size", value: "tire_size" },
            { label: "Wheel Size", value: "wheel_size" },
            { label: "Chain Size", value: "chain_size" },
            { label: "Region / Market", value: "region_market" },
            { label: "Chassis Code", value: "chassis_code" },
            { label: "Production Date", value: "production_date" },
            { label: "Position", value: "position" },
            { label: "Option / Package", value: "option_package" },
        ],
    },
];

const rowStyle = {
    background: "white",
    borderTop: "1px solid #ebebeb",
    padding: "12px 16px",
} as const;

const fieldListStyle = {
    border: "1px solid #ebebeb",
    borderRadius: 8,
    overflow: "hidden",
} as const;

export type FitmentFieldsEditorStatus = {
    hasChanges: boolean;
    loading: boolean;
    saving: boolean;
    save?: () => Promise<void>;
    discard?: () => void;
};

export type FitmentFieldsSettingsSection = "fields" | "referenceData";

type FitmentFieldsSettingsProps = {
    onStatusChange?: (status: FitmentFieldsEditorStatus) => void;
    onOpenVendorIntegrations?: () => void;
    section?: FitmentFieldsSettingsSection;
    showSaveBar?: boolean;
};

type FieldDragState = {
    sourceIndex: number;
    destinationIndex: number;
    pointerX: number;
    pointerY: number;
    offsetX: number;
    offsetY: number;
    width: number;
    portalContainer: HTMLElement;
};

type FieldDragStartEvent = ReactPointerEvent<HTMLElement>;

const fieldGridColumns = "32px minmax(200px, 1.15fr) minmax(200px, 0.9fr) 88px 120px minmax(132px, auto)";

const fieldHeaderGridStyle = {
    alignItems: "center",
    background: "#f7f7f7",
    color: "#616161",
    display: "grid",
    gap: 12,
    gridTemplateColumns: fieldGridColumns,
    padding: "10px 16px",
} as const;

const activeFieldGridStyle = {
    alignItems: "center",
    display: "grid",
    gap: 12,
    gridTemplateColumns: fieldGridColumns,
} as const;

const dragHandleStyle = {
    alignItems: "center",
    cursor: "grab",
    display: "inline-flex",
    height: 28,
    justifyContent: "center",
    touchAction: "none",
    userSelect: "none",
    width: 28,
} as const;

const compactCheckboxHeaderStyle = {
    alignItems: "center",
    display: "flex",
    gap: 4,
    lineHeight: "16px",
    minWidth: 0,
} as const;

const fieldsTitleRowStyle = {
    alignItems: "center",
    display: "flex",
    flexWrap: "nowrap",
    gap: 12,
    justifyContent: "space-between",
} as const;

const draggingRowStyle = {
    background: "white",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.16)",
    opacity: 0.94,
    zIndex: 2147483647,
} as const;

const draggingSourceRowStyle = {
    opacity: 0.35,
} as const;

const compactButtonStyle = {
    minWidth: 32,
} as const;

const referenceDataOptionStyle = {
    alignItems: "flex-start",
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    justifyContent: "space-between",
} as const;

const referenceDataOptionTextStyle = {
    flex: "1 1 420px",
    minWidth: 0,
} as const;

const referenceDataOptionControlStyle = {
    paddingTop: 2,
} as const;

const modalTwoColumnStyle = {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
} as const;

const getModalRadioCardStyle = (isSelected: boolean) => ({
    alignItems: "flex-start",
    background: isSelected ? "var(--p-color-bg-surface-selected)" : "var(--p-color-bg-surface)",
    border: `1px solid ${isSelected ? "var(--p-color-border-focus)" : "var(--p-color-border-secondary)"}`,
    borderRadius: 10,
    boxShadow: isSelected ? "0 0 0 1px var(--p-color-border-focus)" : "none",
    cursor: "pointer",
    display: "flex",
    gap: 10,
    minHeight: 76,
    padding: "12px 14px",
} as const);

type ModalRadioCardProps = {
    checked: boolean;
    description: string;
    id: string;
    label: string;
    name: string;
    onChange: () => void;
};

const ModalRadioCard = ({ checked, description, id, label, name, onChange }: ModalRadioCardProps) => (
    <label htmlFor={id} style={getModalRadioCardStyle(checked)}>
        <input
            id={id}
            type="radio"
            name={name}
            checked={checked}
            onChange={onChange}
            style={{ accentColor: "#005bd3", flexShrink: 0, margin: "3px 0 0" }}
        />
        <span style={{ display: "block", minWidth: 0 }}>
            <Text as="span" variant="bodyMd" fontWeight="semibold">{label}</Text>
            <span style={{ display: "block", marginTop: 2 }}>
                <Text as="span" variant="bodySm" tone="subdued">{description}</Text>
            </span>
        </span>
    </label>
);

const requireValueTooltip = "Fitment records must include a value for this field. Year, Make, and Model are always required for standard fitment compatibility.";
const requiredToSearchTooltip = "Controls whether customers must choose this field before searching.";
const displayLabelTooltip = "Used as the field name across imports and admin screens.";
const fieldTypeTooltip = "Controls how this field is used for imports, matching, and compatibility.";
const valueTypeTooltip = "Controls what kind of values this custom field can store.";
const requireValueModalTooltip = "Fitment records must include a value for this field.";
const requireBeforeSearchModalTooltip = "Customers must choose this field before searching.";
const standardVehicleReferenceDescription = "Adds industry-standard vehicle values such as year, make, model, trim, vehicle type, engine, drivetrain, body details, and region.";
const standardVehicleReferenceBehavior = "Enabling this does not replace your existing fitment data. Leave it off to use only values already saved in your fitment data.";
const turn14ReferenceDescription = "Uses Turn14 fitment records to help match your existing Shopify products to vehicles in the standard vehicle database.";
const turn14ReferenceBehavior = "This does not create Shopify products or manage Shopify inventory.";
const turn14ProductLinkBehavior = "Builds local SKU-to-Turn14 product mappings used by Turn14 product fitment.";
const turn14ShopifyTagBehavior = "When enabled, Turn14 fitments can be written back to Shopify as product vehicle tags.";
const turn14TagLimitBehavior = "When enabled, products that would exceed Shopify's 250-tag limit are omitted from Turn14 vehicle tag sync instead of receiving partial fitment tags.";

const FieldLabel = ({ label, tooltip }: { label: string; tooltip: string }) => (
    <InlineStack gap="100" blockAlign="center" wrap={false}>
        <span>{label}</span>
        <SettingsHelpTooltip content={tooltip} />
    </InlineStack>
);

const standardFieldDefaults: Record<string, { index: number; label: string; required: boolean }> = {
    year: { index: 1, label: "Year", required: true },
    make: { index: 2, label: "Make", required: true },
    model: { index: 3, label: "Model", required: true },
    submodel: { index: 4, label: "Submodel / Trim", required: false },
    engine: { index: 5, label: "Engine", required: false },
    displacement: { index: 6, label: "Displacement", required: false },
    body_vehicle_type: { index: 7, label: "Body / Vehicle Type", required: false },
    drive_type: { index: 8, label: "Drive Type", required: false },
    transmission: { index: 9, label: "Transmission", required: false },
    fuel_type: { index: 10, label: "Fuel Type", required: false },
    cab_type: { index: 11, label: "Cab Type", required: false },
    bed_length: { index: 12, label: "Bed Length", required: false },
    wheelbase: { index: 13, label: "Wheelbase", required: false },
    doors: { index: 14, label: "Doors", required: false },
    brake_type: { index: 15, label: "Brake Type", required: false },
    brake_size: { index: 16, label: "Brake Size", required: false },
    tire_size: { index: 17, label: "Tire Size", required: false },
    wheel_size: { index: 18, label: "Wheel Size", required: false },
    chain_size: { index: 19, label: "Chain Size", required: false },
    region_market: { index: 20, label: "Region / Market", required: false },
    chassis_code: { index: 21, label: "Chassis Code", required: false },
    production_date: { index: 22, label: "Production Date", required: false },
    position: { index: 23, label: "Position", required: false },
    option_package: { index: 24, label: "Option / Package", required: false },
};

const standardFieldKeys = new Set(Object.keys(standardFieldDefaults));
const requiredStandardFieldKeys = new Set(["year", "make", "model"]);
const standardCompatibilityOrders = [
    ["year", "make", "model"],
    ["make", "model", "year"],
];
const defaultTextMaxLength = 200;

const cloneCatalog = (catalog: FitmentCatalogSettings): FitmentCatalogSettings => ({
    ...catalog,
    fields: catalog.fields.map((field) => ({ ...field })),
});

function getReorderedPreview<T>(items: T[], sourceIndex: number, destinationIndex: number) {
    if (sourceIndex === destinationIndex) return items;

    const next = [...items];
    const [movedItem] = next.splice(sourceIndex, 1);
    next.splice(destinationIndex, 0, movedItem);
    return next;
}

const normalizeDisplayOrder = (fields: FitmentFieldDefinition[]) =>
    fields.map((field, index) => ({ ...field, displayOrder: index + 1 }));

const normalizeKey = (value: string) =>
    value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const inferCatalogType = (fields: FitmentFieldDefinition[]) => {
    const enabledKeys = new Set(fields.filter((field) => field.isEnabled).map((field) => normalizeKey(field.key || field.label)));
    return ["year", "make", "model"].every((key) => enabledKeys.has(key)) ? "Hybrid" : "Custom";
};

const getFieldRole = (field: FitmentFieldDefinition) => {
    const key = normalizeKey(field.key || field.label);
    return standardFieldKeys.has(key) ? key : "custom";
};

const isRequiredLockedField = (field: FitmentFieldDefinition) =>
    requiredStandardFieldKeys.has(getFieldRole(field));

const getDefaultSortDirection = (roleOrField: string | FitmentFieldDefinition): FitmentFieldSortDirection =>
    (typeof roleOrField === "string" ? roleOrField : getFieldRole(roleOrField)) === "year" ? "Desc" : "Asc";

const getDefaultSortMode = (): FitmentFieldSortMode => "NaturalNumeric";

const getDefaultRuleState = (role: string, hasStandardFitmentSetup: boolean) => {
    if (requiredStandardFieldKeys.has(role)) {
        return { isRequired: true, isFilterable: true };
    }

    if (role === "custom" && !hasStandardFitmentSetup) {
        return { isRequired: true, isFilterable: true };
    }

    return { isRequired: false, isFilterable: false };
};

const normalizeSimpleFieldSettings = (field: FitmentFieldDefinition): FitmentFieldDefinition => {
    const requiredLocked = isRequiredLockedField(field);
    const isRequired = field.isEnabled ? requiredLocked || field.isRequired : false;

    return {
        ...field,
        isRequired,
        isFilterable: field.isEnabled && isRequired && (requiredLocked || field.isFilterable),
        sortDirection: field.sortDirection ?? getDefaultSortDirection(field),
        sortMode: field.sortMode ?? getDefaultSortMode(),
        maxLength: field.dataType === "Text" ? defaultTextMaxLength : null,
    };
};

const normalizeSimpleCatalogSettings = (catalog: FitmentCatalogSettings): FitmentCatalogSettings => ({
    ...catalog,
    includeVcdbReferenceData: catalog.includeVcdbReferenceData ?? false,
    includeTurn14ReferenceData: catalog.includeTurn14ReferenceData ?? false,
    syncTurn14ProductLinks: catalog.syncTurn14ProductLinks ?? true,
    syncTurn14VehicleTagsToShopify: catalog.syncTurn14VehicleTagsToShopify ?? false,
    skipShopifyVehicleTagSyncWhenOverLimit: catalog.skipShopifyVehicleTagSyncWhenOverLimit ?? true,
    turn14ReferenceDataAvailable: catalog.turn14ReferenceDataAvailable ?? false,
    fields: catalog.fields.map(normalizeSimpleFieldSettings),
});

const getFieldRoleOptions = (fields: FitmentFieldDefinition[], currentIndex: number) => {
    const usedStandardKeys = new Set(
        fields
            .filter((field, index) => index !== currentIndex && field.isEnabled)
            .map((field) => normalizeKey(field.key || field.label))
            .filter((key) => standardFieldKeys.has(key))
    );

    return fieldRoleOptionGroups.map((group) => ({
        ...group,
        options: group.options.map((option) => ({
            ...option,
            disabled: option.value !== "custom" && usedStandardKeys.has(option.value),
        })),
    }));
};

const createBlankField = (displayOrder: number): FitmentFieldDefinition => ({
    fieldIndex: 0,
    key: "",
    label: "",
    dataType: "Text",
    isEnabled: true,
    isRequired: false,
    isFilterable: false,
    isVisible: true,
    displayOrder,
    maxLength: defaultTextMaxLength,
    sortDirection: "Asc",
    sortMode: getDefaultSortMode(),
    placeholder: "",
    helpText: "",
});

const createFieldFromRole = (displayOrder: number, role: string, hasStandardFitmentSetup = false): FitmentFieldDefinition => {
    const blankField = createBlankField(displayOrder);
    const defaults = standardFieldDefaults[role];
    const ruleState = getDefaultRuleState(role, hasStandardFitmentSetup);

    if (!defaults) {
        return normalizeSimpleFieldSettings({
            ...blankField,
            ...ruleState,
        });
    }

    return normalizeSimpleFieldSettings({
        ...blankField,
        key: role,
        label: defaults.label,
        ...ruleState,
        sortDirection: getDefaultSortDirection(role),
    });
};

const getActiveCatalog = (catalog: FitmentCatalogSettings): FitmentCatalogSettings => ({
    ...catalog,
    fields: normalizeDisplayOrder(catalog.fields.filter((field) => field.isEnabled)),
});

const isRoleAlreadyUsed = (fields: FitmentFieldDefinition[], role: string, currentIndex = -1) =>
    role !== "custom" && fields.some((field, index) =>
        index !== currentIndex && field.isEnabled && getFieldRole(field) === role
    );

const getStandardFitmentCompatibility = (fields: FitmentFieldDefinition[]) => {
    const activeRoles = fields.filter((field) => field.isEnabled).map(getFieldRole);
    const activeByRole = new Map(
        fields
            .filter((field) => field.isEnabled)
            .map((field) => [getFieldRole(field), field])
    );
    const issues: string[] = [];
    const hasStandardOrder = standardCompatibilityOrders.some((order) =>
        order.every((role, index) => activeRoles[index] === role)
    );

    if (!hasStandardOrder) {
        issues.push("Year, Make, and Model must be the first fields in Year-Make-Model or Make-Model-Year order");
    }

    requiredStandardFieldKeys.forEach((role) => {
        const label = standardFieldDefaults[role].label;

        if (!activeByRole.has(role)) {
            issues.push(`${label} is missing`);
            return;
        }

        if (!activeByRole.get(role)?.isRequired) {
            issues.push(`${label} must remain required`);
        }
    });

    const compatible = issues.length === 0;

    return {
        compatible,
        label: compatible ? "Standard fitment" : "Custom setup",
        tone: compatible ? "success" as const : undefined,
        tooltip: compatible
            ? "Year, Make, and Model are active and mapped as the first fields in a standard order. This keeps ACES/PIES-style imports, Turn14 fitment, and similar fitment standards available."
            : "This setup can be used for custom selectors. Add Year, Make, and Model field types to enable standard fitment compatibility.",
    };
};

const FitmentFieldsSettings: React.FC<FitmentFieldsSettingsProps> = ({
    onStatusChange,
    onOpenVendorIntegrations,
    section = "fields",
    showSaveBar = true,
}) => {
    const [draft, setDraft] = useState<FitmentCatalogSettings | undefined>();
    const [baseline, setBaseline] = useState<FitmentCatalogSettings | undefined>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [addFieldModalOpen, setAddFieldModalOpen] = useState(false);
    const [addFieldDraft, setAddFieldDraft] = useState<FitmentFieldDefinition | undefined>();
    const [editingFieldIndex, setEditingFieldIndex] = useState<number | undefined>();
    const [fieldDrag, setFieldDrag] = useState<FieldDragState | undefined>();
    const activeFieldsRef = useRef<{ field: FitmentFieldDefinition; originalIndex: number }[]>([]);
    const fieldDragRef = useRef<FieldDragState | undefined>();
    const fieldRowRefs = useRef(new Map<number, HTMLDivElement>());
    const dragCleanupRef = useRef<(() => void) | undefined>();
    const [error, setError] = useState<string | undefined>();
    const [success, setSuccess] = useState<string | undefined>();

    useEffect(() => {
        const loadCatalog = async () => {
            setLoading(true);
            setError(undefined);

            try {
                const catalog = await agent.Settings.fitmentCatalog();
                const normalizedCatalog = {
                    ...catalog,
                    name: catalog.name === "Default fitment catalog" ? "Vehicles" : catalog.name,
                };
                const simpleCatalog = getActiveCatalog(normalizeSimpleCatalogSettings(normalizedCatalog));
                setCachedFitmentCatalogLabel(simpleCatalog.name);
                setDraft(cloneCatalog(simpleCatalog));
                setBaseline(cloneCatalog(simpleCatalog));
            } catch (err: any) {
                setError(err?.message ?? "Failed to load fitment fields.");
            } finally {
                setLoading(false);
            }
        };

        loadCatalog();
    }, []);

    const hasUnsavedChanges = useMemo(
        () => !!draft && !!baseline && JSON.stringify(draft) !== JSON.stringify(baseline),
        [draft, baseline]
    );

    const indexedFields = useMemo(
        () => draft?.fields.map((field, originalIndex) => ({ field, originalIndex })) ?? [],
        [draft]
    );

    const activeFields = useMemo(
        () => indexedFields.filter(({ field }) => field.isEnabled),
        [indexedFields]
    );
    const displayedActiveFields = useMemo(
        () => fieldDrag
            ? getReorderedPreview(activeFields, fieldDrag.sourceIndex, fieldDrag.destinationIndex)
            : activeFields,
        [activeFields, fieldDrag]
    );
    const draggedActiveField = fieldDrag ? activeFields[fieldDrag.sourceIndex] : undefined;

    useEffect(() => {
        activeFieldsRef.current = activeFields;
    }, [activeFields]);

    useEffect(() => () => {
        dragCleanupRef.current?.();
    }, []);

    const enabledFieldCount = activeFields.length;
    const standardFitmentCompatibility = useMemo(
        () => getStandardFitmentCompatibility(draft?.fields ?? []),
        [draft?.fields]
    );
    const addFieldRole = addFieldDraft ? getFieldRole(addFieldDraft) : "custom";
    const addFieldRoleOptions = useMemo(
        () => draft ? getFieldRoleOptions(draft.fields, editingFieldIndex ?? -1) : fieldRoleOptionGroups,
        [draft, editingFieldIndex]
    );
    const addFieldRoleAlreadyUsed = useMemo(
        () => !!draft && !!addFieldDraft && isRoleAlreadyUsed(draft.fields, addFieldRole, editingFieldIndex ?? -1),
        [addFieldDraft, addFieldRole, draft, editingFieldIndex]
    );
    const editingExistingField = editingFieldIndex !== undefined;
    const canOpenAddField = !!draft && enabledFieldCount < draft.maxFields;
    const canConfirmAddField = !!draft
        && !!addFieldDraft
        && (editingExistingField || enabledFieldCount < draft.maxFields)
        && !addFieldRoleAlreadyUsed
        && !!addFieldDraft.label.trim();

    const updateCatalog = useCallback((changes: Partial<FitmentCatalogSettings>) => {
        setSuccess(undefined);
        setDraft((current) => current ? ({ ...current, ...changes }) : current);
    }, []);

    const updateField = useCallback((index: number, changes: Partial<FitmentFieldDefinition>) => {
        setSuccess(undefined);
        setDraft((current) => {
            if (!current) return current;

            const fields = current.fields.map((field, fieldIndex) =>
                fieldIndex === index ? normalizeSimpleFieldSettings({ ...field, ...changes }) : field
            );

            return { ...current, catalogType: inferCatalogType(fields), fields };
        });
    }, []);

    const updateFieldRole = useCallback((index: number, role: string) => {
        setSuccess(undefined);
        setDraft((current) => {
            if (!current) return current;

            const fields = current.fields.map((field, fieldIndex) => {
                if (fieldIndex !== index) return field;

                const currentRole = getFieldRole(field);
                if (role === "custom") {
                    const ruleState = currentRole === "custom"
                        ? { isRequired: field.isRequired, isFilterable: field.isFilterable }
                        : getDefaultRuleState(role, getStandardFitmentCompatibility(current.fields).compatible);

                    return normalizeSimpleFieldSettings({
                        ...field,
                        key: currentRole === "custom" ? field.key : `custom_${field.fieldIndex || index + 1}`,
                        dataType: field.dataType || "Text",
                        ...ruleState,
                        sortDirection: currentRole === "custom" ? field.sortDirection : getDefaultSortDirection(role),
                    });
                }

                const defaults = standardFieldDefaults[role];
                const ruleState = getDefaultRuleState(role, getStandardFitmentCompatibility(current.fields).compatible);

                return normalizeSimpleFieldSettings({
                    ...field,
                    key: role,
                    label: !field.label.trim() || currentRole !== "custom" ? defaults.label : field.label,
                    dataType: "Text" as FitmentFieldDataType,
                    ...ruleState,
                    sortDirection: getDefaultSortDirection(role),
                    isVisible: true,
                    isEnabled: true,
                    maxLength: defaultTextMaxLength,
                });
            });

            return { ...current, catalogType: inferCatalogType(fields), fields };
        });
    }, []);

    const moveActiveField = useCallback((fromIndex: number, toIndex: number) => {
        setSuccess(undefined);
        setDraft((current) => {
            const active = current?.fields.filter((field) => field.isEnabled) ?? [];
            if (!current || fromIndex === toIndex || toIndex < 0 || toIndex >= active.length) {
                return current;
            }

            const inactive = current.fields.filter((field) => !field.isEnabled);
            const [field] = active.splice(fromIndex, 1);
            active.splice(toIndex, 0, field);

            // Keep disabled fields available, but out of the main selector order.
            const fields = [...active, ...inactive];
            return { ...current, catalogType: inferCatalogType(fields), fields: normalizeDisplayOrder(fields) };
        });
    }, []);

    const openAddFieldModal = useCallback(() => {
        if (!draft || enabledFieldCount >= draft.maxFields) return;

        const nextOrder = enabledFieldCount + 1;
        setAddFieldDraft(createFieldFromRole(nextOrder, "custom", standardFitmentCompatibility.compatible));
        setEditingFieldIndex(undefined);
        setAddFieldModalOpen(true);
        setError(undefined);
    }, [draft, enabledFieldCount, standardFitmentCompatibility.compatible]);

    const openEditFieldModal = useCallback((index: number) => {
        if (!draft?.fields[index]) return;

        setAddFieldDraft({ ...draft.fields[index] });
        setEditingFieldIndex(index);
        setAddFieldModalOpen(true);
        setError(undefined);
    }, [draft]);

    const closeAddFieldModal = useCallback(() => {
        setAddFieldModalOpen(false);
        setAddFieldDraft(undefined);
        setEditingFieldIndex(undefined);
    }, []);

    const updateAddFieldDraft = useCallback((changes: Partial<FitmentFieldDefinition>) => {
        setAddFieldDraft((current) => current ? normalizeSimpleFieldSettings({ ...current, ...changes }) : current);
    }, []);

    const updateAddFieldRole = useCallback((role: string) => {
        setAddFieldDraft((current) => {
            if (!current) return current;

            const currentRole = getFieldRole(current);
            if (role === "custom") {
                const ruleState = currentRole === "custom"
                    ? { isRequired: current.isRequired, isFilterable: current.isFilterable }
                    : getDefaultRuleState(role, standardFitmentCompatibility.compatible);

                return normalizeSimpleFieldSettings({
                    ...current,
                    key: currentRole === "custom" ? current.key : `custom_${current.fieldIndex || current.displayOrder || 1}`,
                    dataType: current.dataType || "Text",
                    ...ruleState,
                    sortDirection: currentRole === "custom" ? current.sortDirection : getDefaultSortDirection(role),
                });
            }

            const defaults = standardFieldDefaults[role];
            const ruleState = getDefaultRuleState(role, standardFitmentCompatibility.compatible);

            return normalizeSimpleFieldSettings({
                ...current,
                key: role,
                label: !current.label.trim() || currentRole !== "custom" ? defaults.label : current.label,
                dataType: "Text" as FitmentFieldDataType,
                ...ruleState,
                sortDirection: getDefaultSortDirection(role),
                isVisible: true,
                isEnabled: true,
                maxLength: defaultTextMaxLength,
            });
        });
    }, [standardFitmentCompatibility.compatible]);

    const addField = useCallback(() => {
        setSuccess(undefined);
        if (!addFieldDraft) return;

        setDraft((current) => {
            if (!current) {
                return current;
            }

            if (editingFieldIndex === undefined && current.fields.filter((field) => field.isEnabled).length >= current.maxFields) {
                return current;
            }

            if (isRoleAlreadyUsed(current.fields, getFieldRole(addFieldDraft), editingFieldIndex ?? -1)) {
                return current;
            }

            if (editingFieldIndex !== undefined) {
                const fields = current.fields.map((field, index) => {
                    if (index !== editingFieldIndex) return field;

                    return normalizeSimpleFieldSettings({
                        ...field,
                        ...addFieldDraft,
                        id: field.id,
                        fieldIndex: field.fieldIndex,
                        displayOrder: field.displayOrder,
                        isEnabled: true,
                        isVisible: true,
                    });
                });

                return { ...current, catalogType: inferCatalogType(fields), fields };
            }

            const active = current.fields.filter((field) => field.isEnabled);
            const fieldToAdd = normalizeSimpleFieldSettings({
                ...addFieldDraft,
                displayOrder: active.length + 1,
                fieldIndex: 0,
                isEnabled: true,
                isVisible: true,
            });

            const fields = normalizeDisplayOrder([
                ...active,
                fieldToAdd,
            ]);

            return { ...current, catalogType: inferCatalogType(fields), fields };
        });
        closeAddFieldModal();
    }, [addFieldDraft, closeAddFieldModal, editingFieldIndex]);

    const removeOrDisableField = useCallback((index: number) => {
        setSuccess(undefined);
        setDraft((current) => {
            if (!current) return current;

            const fields = current.fields.filter((_, fieldIndex) => fieldIndex !== index);

            const normalizedFields = normalizeDisplayOrder(fields);
            return { ...current, catalogType: inferCatalogType(normalizedFields), fields: normalizedFields };
        });
    }, []);

    const discardChanges = useCallback(() => {
        if (!baseline) return;

        setDraft(cloneCatalog(baseline));
        setError(undefined);
        setSuccess(undefined);
        setAddFieldModalOpen(false);
        setAddFieldDraft(undefined);
        setEditingFieldIndex(undefined);
    }, [baseline]);

    const validate = useCallback(() => {
        if (!draft) return "Fitment catalog is not loaded.";

        const enabledFields = draft.fields.filter((field) => field.isEnabled);
        if (!enabledFields.length) return "At least one field must be enabled.";
        if (enabledFields.some((field) => !field.label.trim())) return "Enabled fields need a label.";

        const labels = enabledFields.map((field) => field.label.trim().toLowerCase());
        if (labels.some((label, index) => labels.indexOf(label) !== index)) {
            return "Enabled field labels must be unique.";
        }

        const standardRoles = enabledFields
            .map((field) => getFieldRole(field))
            .filter((role) => role !== "custom");
        if (standardRoles.some((role, index) => standardRoles.indexOf(role) !== index)) {
            return "Standard fitment field types can each be assigned to only one field.";
        }

        return undefined;
    }, [draft]);

    const saveChanges = useCallback(async () => {
        const validationError = validate();
        if (validationError || !draft) {
            setError(validationError);
            return;
        }

        setSaving(true);
        setError(undefined);
        setSuccess(undefined);

        try {
            const catalogToSave = getActiveCatalog(normalizeSimpleCatalogSettings(draft));
            const saved = getActiveCatalog(normalizeSimpleCatalogSettings(await agent.Settings.updateFitmentCatalog(catalogToSave)));
            setDraft(cloneCatalog(saved));
            setBaseline(cloneCatalog(saved));
            setCachedFitmentCatalogLabel(saved.name);
            setAddFieldModalOpen(false);
            setAddFieldDraft(undefined);
            setEditingFieldIndex(undefined);
            setSuccess(section === "referenceData" ? "Fitment data source settings saved." : "Fitment fields saved.");
        } catch (err: any) {
            setError(err?.response?.data ?? err?.message ?? (section === "referenceData" ? "Failed to save fitment data source settings." : "Failed to save fitment fields."));
        } finally {
            setSaving(false);
        }
    }, [draft, section, validate]);

    useEffect(() => {
        onStatusChange?.({
            hasChanges: hasUnsavedChanges,
            loading,
            saving,
            save: saveChanges,
            discard: discardChanges,
        });
    }, [discardChanges, hasUnsavedChanges, loading, onStatusChange, saveChanges, saving]);

    const startFieldDrag = useCallback((event: FieldDragStartEvent, sourceIndex: number) => {
        if (event.button !== 0 || fieldDragRef.current) return;

        const row = fieldRowRefs.current.get(sourceIndex)
            ?? event.currentTarget.closest("[data-fitment-field-row]") as HTMLDivElement | null;
        if (!row) return;

        const ownerDocument = row.ownerDocument;
        const ownerWindow = ownerDocument.defaultView;
        const previousBodyUserSelect = ownerDocument.body.style.userSelect;
        const rect = row.getBoundingClientRect();

        const getDestinationIndex = (clientY: number) => {
            const fields = activeFieldsRef.current;
            let targetSlot = fields.length;

            for (let index = 0; index < fields.length; index += 1) {
                const targetRow = fieldRowRefs.current.get(index);
                if (!targetRow) continue;

                const targetRect = targetRow.getBoundingClientRect();
                if (clientY < targetRect.top + targetRect.height / 2) {
                    targetSlot = index;
                    break;
                }
            }

            return Math.max(0, Math.min(fields.length - 1, targetSlot));
        };

        const updateDrag = (clientX: number, clientY: number) => {
            setFieldDrag((current) => {
                if (!current) return current;

                const next = {
                    ...current,
                    destinationIndex: getDestinationIndex(clientY),
                    pointerX: clientX,
                    pointerY: clientY,
                };
                fieldDragRef.current = next;
                return next;
            });
        };

        const cleanup = () => {
            ownerDocument.removeEventListener("pointermove", handlePointerMove, true);
            ownerDocument.removeEventListener("pointerup", handlePointerUp, true);
            ownerDocument.removeEventListener("pointercancel", handlePointerCancel, true);
            ownerWindow?.removeEventListener("blur", handlePointerCancel, true);
            ownerDocument.body.style.userSelect = previousBodyUserSelect;
            dragCleanupRef.current = undefined;
        };

        const finishDrag = () => {
            const current = fieldDragRef.current;
            cleanup();
            fieldDragRef.current = undefined;
            setFieldDrag(undefined);

            if (current && current.destinationIndex !== current.sourceIndex) {
                moveActiveField(current.sourceIndex, current.destinationIndex);
            }
        };

        const cancelDrag = () => {
            cleanup();
            fieldDragRef.current = undefined;
            setFieldDrag(undefined);
        };

        function handlePointerMove(moveEvent: PointerEvent) {
            moveEvent.preventDefault();
            updateDrag(moveEvent.clientX, moveEvent.clientY);
        }

        function handlePointerUp() {
            finishDrag();
        }

        function handlePointerCancel() {
            cancelDrag();
        }

        event.preventDefault();
        event.stopPropagation();

        ownerDocument.body.style.userSelect = "none";
        ownerDocument.addEventListener("pointermove", handlePointerMove, true);
        ownerDocument.addEventListener("pointerup", handlePointerUp, true);
        ownerDocument.addEventListener("pointercancel", handlePointerCancel, true);
        ownerWindow?.addEventListener("blur", handlePointerCancel, true);

        const dragState = {
            sourceIndex,
            destinationIndex: sourceIndex,
            pointerX: event.clientX,
            pointerY: event.clientY,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width,
            portalContainer: ownerDocument.body,
        };

        dragCleanupRef.current = cleanup;
        fieldDragRef.current = dragState;
        setFieldDrag(dragState);
    }, [moveActiveField]);

    if (loading || !draft) {
        return (
            <Layout.Section>
                <Card>
                    <Box padding="400">
                        <SkeletonBodyText lines={8} />
                    </Box>
                </Card>
            </Layout.Section>
        );
    }

    const renderFieldRow = (
        field: FitmentFieldDefinition,
        originalIndex: number,
        activeIndex: number,
        options: {
            isDragging?: boolean;
            isPreview?: boolean;
            onHandleDragStart?: (event: FieldDragStartEvent) => void;
            rowKey?: string;
            setRef?: (node: HTMLDivElement | null) => void;
        } = {}
    ) => {
        const fieldRole = getFieldRole(field);
        const requiredLocked = isRequiredLockedField(field);
        const requireBeforeSearchDisabled = requiredLocked || !field.isRequired;
        const roleOptions = getFieldRoleOptions(draft.fields, originalIndex);
        const fieldLabel = field.label.trim() || `field ${activeIndex + 1}`;

        return (
            <div
                data-fitment-field-row
                key={options.rowKey}
                ref={options.setRef}
                style={{
                    ...rowStyle,
                    ...(options.isDragging ? draggingSourceRowStyle : {}),
                    ...(options.isPreview ? draggingRowStyle : {}),
                }}
            >
                <div style={activeFieldGridStyle}>
                    <InlineStack blockAlign="center">
                        <span
                            aria-label={`Drag ${fieldLabel} to reorder`}
                            onPointerDown={options.onHandleDragStart}
                            role="button"
                            style={{
                                ...dragHandleStyle,
                                cursor: options.isDragging || options.isPreview ? "grabbing" : "grab",
                            }}
                            tabIndex={0}
                            title="Drag to reorder"
                        >
                            <DragHandleIcon width={20} height={20} />
                        </span>
                    </InlineStack>

                    <TextField
                        label="Display label"
                        labelHidden
                        autoComplete="off"
                        value={field.label}
                        placeholder={`Field ${originalIndex + 1}`}
                        onChange={(value) => updateField(originalIndex, { label: value })}
                    />

                    <Select
                        label="Field type"
                        labelHidden
                        options={roleOptions}
                        value={fieldRole}
                        onChange={(value) => updateFieldRole(originalIndex, value)}
                    />
                    {/* Custom value type stays in the Settings modal so the list row has one field-type control. */}

                    <div>
                        {requiredLocked ? (
                            <SettingsTooltip content={requireValueTooltip}>
                                <div>
                                    <Checkbox
                                        label="Require value"
                                        labelHidden
                                        checked
                                        disabled
                                        onChange={() => undefined}
                                    />
                                </div>
                            </SettingsTooltip>
                        ) : (
                            <SettingsTooltip content={requireValueTooltip}>
                                <div>
                                    <Checkbox
                                        label="Require value"
                                        labelHidden
                                        checked={field.isRequired}
                                        onChange={(checked) => updateField(originalIndex, { isRequired: checked })}
                                    />
                                </div>
                            </SettingsTooltip>
                        )}
                    </div>

                    <div>
                        <SettingsTooltip content={requiredToSearchTooltip}>
                            <div>
                                <Checkbox
                                    label="Require before search"
                                    labelHidden
                                    checked={field.isFilterable}
                                    disabled={requireBeforeSearchDisabled}
                                    onChange={(checked) => updateField(originalIndex, { isFilterable: checked })}
                                />
                            </div>
                        </SettingsTooltip>
                    </div>

                    <div>
                        <InlineStack gap="100" blockAlign="center" wrap={false}>
                            <Button
                                variant="tertiary"
                                onClick={() => openEditFieldModal(originalIndex)}
                            >
                                Settings
                            </Button>
                            <SettingsTooltip content={field.id ? "Disable field" : "Remove row"}>
                                <div style={compactButtonStyle}>
                                    <Button
                                        icon={DeleteIcon}
                                        variant="tertiary"
                                        tone="critical"
                                        accessibilityLabel={field.id ? "Disable field" : "Remove row"}
                                        onClick={() => removeOrDisableField(originalIndex)}
                                    />
                                </div>
                            </SettingsTooltip>
                        </InlineStack>
                    </div>
                </div>
            </div>
        );
    };

    const saveBarMarkup = showSaveBar ? (
        <SaveBar id="fitment-fields-save-bar" open={hasUnsavedChanges}>
            <button onClick={discardChanges} disabled={saving}>
                Discard
            </button>
            <button variant="primary" onClick={saveChanges} disabled={saving} loading={saving}>
                Save
            </button>
        </SaveBar>
    ) : null;

    if (section === "referenceData") {
        return (
            <>
                {saveBarMarkup}

                <Layout.Section>
                    <BlockStack gap="400">
                        {error ? <Banner tone="critical">{error}</Banner> : null}
                        {success ? <Banner tone="success">{success}</Banner> : null}

                        <Card>
                            <Box padding="400">
                                <BlockStack gap="400">
                                    <div style={referenceDataOptionStyle}>
                                        <div style={referenceDataOptionTextStyle}>
                                            <BlockStack gap="300">
                                                <InlineStack gap="200" blockAlign="center">
                                                    <Text as="h3" variant="headingSm">Standard vehicle database</Text>
                                                    <Badge tone="success">Available</Badge>
                                                </InlineStack>
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {standardVehicleReferenceDescription}
                                                </Text>
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {standardVehicleReferenceBehavior}
                                                </Text>
                                            </BlockStack>
                                        </div>
                                        <div style={referenceDataOptionControlStyle}>
                                            <Checkbox
                                                label="Enable standard vehicle fitment database"
                                                labelHidden
                                                checked={draft.includeVcdbReferenceData ?? false}
                                                onChange={(checked) => updateCatalog({ includeVcdbReferenceData: checked })}
                                            />
                                        </div>
                                    </div>

                                    <Divider />

                                    <div style={referenceDataOptionStyle}>
                                        <div style={referenceDataOptionTextStyle}>
                                            <BlockStack gap="300">
                                                <InlineStack gap="200" blockAlign="center">
                                                    <Text as="h3" variant="headingSm">Turn14 fitment data</Text>
                                                    {draft.turn14ReferenceDataAvailable
                                                        ? <Badge tone="success">Available</Badge>
                                                        : <Badge tone="attention">Requires Turn14 connection</Badge>}
                                                </InlineStack>
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {turn14ReferenceDescription}
                                                </Text>
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {turn14ReferenceBehavior}
                                                </Text>
                                                {onOpenVendorIntegrations ? (
                                                    <InlineStack align="start">
                                                        <Button onClick={onOpenVendorIntegrations}>
                                                            Manage Turn14 integration
                                                        </Button>
                                                    </InlineStack>
                                                ) : null}
                                                <Checkbox
                                                    label="Sync Turn14 product links"
                                                    helpText={turn14ProductLinkBehavior}
                                                    checked={(draft.syncTurn14ProductLinks ?? true) && (draft.includeTurn14ReferenceData ?? false)}
                                                    disabled={!(draft.includeTurn14ReferenceData ?? false) || !draft.turn14ReferenceDataAvailable}
                                                    onChange={(checked) => updateCatalog({
                                                        syncTurn14ProductLinks: checked,
                                                        syncTurn14VehicleTagsToShopify: checked
                                                            ? draft.syncTurn14VehicleTagsToShopify
                                                            : false,
                                                    })}
                                                />
                                                <Checkbox
                                                    label="Sync Turn14 vehicle tags to Shopify"
                                                    helpText={turn14ShopifyTagBehavior}
                                                    checked={(draft.syncTurn14VehicleTagsToShopify ?? false) && (draft.includeTurn14ReferenceData ?? false) && (draft.syncTurn14ProductLinks ?? true)}
                                                    disabled={!(draft.includeTurn14ReferenceData ?? false) || !(draft.syncTurn14ProductLinks ?? true) || !draft.turn14ReferenceDataAvailable}
                                                    onChange={(checked) => updateCatalog({ syncTurn14VehicleTagsToShopify: checked })}
                                                />
                                                <Checkbox
                                                    label="Skip products over Shopify's tag limit"
                                                    helpText={turn14TagLimitBehavior}
                                                    checked={draft.skipShopifyVehicleTagSyncWhenOverLimit ?? true}
                                                    onChange={(checked) => updateCatalog({ skipShopifyVehicleTagSyncWhenOverLimit: checked })}
                                                />
                                            </BlockStack>
                                        </div>
                                        <div style={referenceDataOptionControlStyle}>
                                            <Checkbox
                                                label="Enable Turn14 fitment data"
                                                labelHidden
                                                checked={(draft.includeTurn14ReferenceData ?? false) && !!draft.turn14ReferenceDataAvailable}
                                                disabled={!draft.turn14ReferenceDataAvailable}
                                                onChange={(checked) => updateCatalog({ includeTurn14ReferenceData: checked })}
                                            />
                                        </div>
                                    </div>
                                </BlockStack>
                            </Box>
                        </Card>
                    </BlockStack>
                </Layout.Section>
            </>
        );
    }

    return (
        <>
            {saveBarMarkup}

            <Layout.Section>
                <Card>
                    <Box padding="400">
                        <BlockStack gap="400">
                            <InlineStack align="start" blockAlign="center" gap="300">
                                <BlockStack gap="100">
                                    <Text as="h2" variant="headingMd">Selector field setup</Text>
                                    <Text as="p" variant="bodySm" tone="subdued">
                                        {enabledFieldCount} of {draft.maxFields} fields enabled for storefront selectors, vehicle records, imports, and product mappings.
                                    </Text>
                                </BlockStack>
                            </InlineStack>

                            {error ? <Banner tone="critical">{error}</Banner> : null}
                            {success ? <Banner tone="success">{success}</Banner> : null}

                            <TextField
                                label="Catalog name"
                                autoComplete="off"
                                value={draft.name}
                                placeholder="Vehicles"
                                onChange={(value) => updateCatalog({ name: value })}
                            />

                            <Divider />

                            <BlockStack gap="100">
                                <div style={fieldsTitleRowStyle}>
                                    <Text as="h3" variant="headingSm">Fields</Text>
                                    <SettingsTooltip content={standardFitmentCompatibility.tooltip}>
                                        <Badge tone={standardFitmentCompatibility.tone}>{standardFitmentCompatibility.label}</Badge>
                                    </SettingsTooltip>
                                </div>
                                <Text as="span" variant="bodySm" tone="subdued">
                                    Drag fields to change storefront and admin display order. Labels are shown to customers; field types control imports, matching, and compatibility.
                                </Text>
                            </BlockStack>

                            <div style={fieldListStyle}>
                                <div style={fieldHeaderGridStyle}>
                                    <div />
                                    <Text as="span" variant="bodySm" fontWeight="medium">Display label</Text>
                                    <Text as="span" variant="bodySm" fontWeight="medium">Field type</Text>
                                    <div style={compactCheckboxHeaderStyle}>
                                        <Text as="span" variant="bodySm" fontWeight="medium">Require<br />value</Text>
                                        <SettingsHelpTooltip content={requireValueTooltip} />
                                    </div>
                                    <div style={compactCheckboxHeaderStyle}>
                                        <Text as="span" variant="bodySm" fontWeight="medium">Require before<br />search</Text>
                                        <SettingsHelpTooltip content={requiredToSearchTooltip} />
                                    </div>
                                    <Text as="span" variant="bodySm" fontWeight="medium">Actions</Text>
                                </div>

                                {activeFields.length === 0 ? (
                                    <div style={rowStyle}>
                                        <Text as="p" tone="subdued">No fields are currently enabled.</Text>
                                    </div>
                                ) : null}

                                {displayedActiveFields.map(({ field, originalIndex }, index) =>
                                    renderFieldRow(field, originalIndex, index, {
                                        isDragging: !!draggedActiveField && originalIndex === draggedActiveField.originalIndex,
                                        onHandleDragStart: fieldDrag ? undefined : (event) => startFieldDrag(event, index),
                                        rowKey: field.id ? `field-${field.id}` : `new-field-${originalIndex}`,
                                        setRef: (node) => {
                                            if (node) {
                                                fieldRowRefs.current.set(index, node);
                                            } else {
                                                fieldRowRefs.current.delete(index);
                                            }
                                        },
                                    })
                                )}
                            </div>

                            {fieldDrag ? createPortal((() => {
                                const item = activeFields[fieldDrag.sourceIndex];
                                if (!item) return null;

                                return (
                                    <div
                                        style={{
                                            left: fieldDrag.pointerX - fieldDrag.offsetX,
                                            pointerEvents: "none",
                                            position: "fixed",
                                            top: fieldDrag.pointerY - fieldDrag.offsetY,
                                            width: fieldDrag.width,
                                            zIndex: 2147483647,
                                        }}
                                    >
                                        {renderFieldRow(item.field, item.originalIndex, fieldDrag.sourceIndex, {
                                            isPreview: true,
                                        })}
                                    </div>
                                );
                            })(), fieldDrag.portalContainer) : null}

                            <InlineStack align="start" blockAlign="center" gap="200">
                                <Button icon={PlusIcon} onClick={openAddFieldModal} disabled={!canOpenAddField}>
                                    Add field
                                </Button>
                            </InlineStack>
                        </BlockStack>
                    </Box>
                </Card>
            </Layout.Section>

            {addFieldModalOpen ? (
                <div className="pf-field-modal" role="presentation">
                    <button
                        type="button"
                        className="pf-field-modal__backdrop"
                        aria-label="Close field settings"
                        onClick={closeAddFieldModal}
                    />
                    <section
                        className="pf-field-modal__panel"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pf-field-modal-title"
                    >
                        <Card padding="0">
                            <Box padding="400">
                                <InlineStack align="space-between" blockAlign="center" gap="300">
                                    <Text as="h2" variant="headingMd" id="pf-field-modal-title">
                                        {editingExistingField ? "Edit fitment field" : "Add fitment field"}
                                    </Text>
                                    <Button variant="tertiary" onClick={closeAddFieldModal}>
                                        Cancel
                                    </Button>
                                </InlineStack>
                            </Box>
                            <Divider />
                            <Box padding="400">
                                {addFieldDraft ? (
                                    <BlockStack gap="400">
                                        <BlockStack gap="300">
                                            <Text as="h3" variant="headingSm">Field details</Text>
                                            <Select
                                                label={<FieldLabel label="Field type" tooltip={fieldTypeTooltip} />}
                                                options={addFieldRoleOptions}
                                                value={addFieldRole}
                                                onChange={updateAddFieldRole}
                                            />
                                            <TextField
                                                label={<FieldLabel label="Display label" tooltip={displayLabelTooltip} />}
                                                autoComplete="off"
                                                value={addFieldDraft.label}
                                                placeholder="Example: Engine"
                                                onChange={(value) => updateAddFieldDraft({ label: value })}
                                            />
                                            {addFieldRole === "custom" ? (
                                                <Select
                                                    label={<FieldLabel label="Value type" tooltip={valueTypeTooltip} />}
                                                    options={dataTypeOptions}
                                                    value={addFieldDraft.dataType}
                                                    onChange={(value) => updateAddFieldDraft({ dataType: value as FitmentFieldDataType })}
                                                />
                                            ) : null}
                                            <BlockStack gap="200">
                                                <Text as="h4" variant="headingSm">Sort mode</Text>
                                                <div role="radiogroup" aria-label="Sort mode" style={modalTwoColumnStyle}>
                                                    {sortModeOptions.map((option) => (
                                                        <ModalRadioCard
                                                            key={option.value}
                                                            id={`fitment-field-sort-mode-${option.value}`}
                                                            name="fitment-field-sort-mode"
                                                            label={option.label}
                                                            description={option.description}
                                                            checked={(addFieldDraft.sortMode ?? getDefaultSortMode()) === option.value}
                                                            onChange={() => updateAddFieldDraft({ sortMode: option.value as FitmentFieldSortMode })}
                                                        />
                                                    ))}
                                                </div>
                                            </BlockStack>
                                            <BlockStack gap="200">
                                                <Text as="h4" variant="headingSm">Sort order</Text>
                                                <div role="radiogroup" aria-label="Sort order" style={modalTwoColumnStyle}>
                                                    {sortDirectionOptions.map((option) => (
                                                        <ModalRadioCard
                                                            key={option.value}
                                                            id={`fitment-field-sort-order-${option.value}`}
                                                            name="fitment-field-sort-order"
                                                            label={option.label}
                                                            description={option.description}
                                                            checked={(addFieldDraft.sortDirection ?? getDefaultSortDirection(addFieldDraft)) === option.value}
                                                            onChange={() => updateAddFieldDraft({ sortDirection: option.value as FitmentFieldSortDirection })}
                                                        />
                                                    ))}
                                                </div>
                                            </BlockStack>
                                        </BlockStack>

                                        <Divider />

                                        <BlockStack gap="300">
                                            <Text as="h3" variant="headingSm">Rules</Text>
                                            <div style={modalTwoColumnStyle}>
                                                {isRequiredLockedField(addFieldDraft) ? (
                                                    <Checkbox
                                                        label={<FieldLabel label="Require value" tooltip={requireValueModalTooltip} />}
                                                        checked
                                                        disabled
                                                        onChange={() => undefined}
                                                    />
                                                ) : (
                                                    <Checkbox
                                                        label={<FieldLabel label="Require value" tooltip={requireValueModalTooltip} />}
                                                        checked={addFieldDraft.isRequired}
                                                        onChange={(checked) => updateAddFieldDraft({ isRequired: checked })}
                                                    />
                                                )}
                                                <Checkbox
                                                    label={<FieldLabel label="Require before search" tooltip={requireBeforeSearchModalTooltip} />}
                                                    checked={addFieldDraft.isFilterable}
                                                    disabled={isRequiredLockedField(addFieldDraft) || !addFieldDraft.isRequired}
                                                    onChange={(checked) => updateAddFieldDraft({ isFilterable: checked })}
                                                />
                                            </div>
                                        </BlockStack>

                                    </BlockStack>
                                ) : null}
                            </Box>
                            <Divider />
                            <Box padding="400">
                                <InlineStack align="end" gap="200">
                                    <Button onClick={closeAddFieldModal}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" onClick={addField} disabled={!canConfirmAddField}>
                                        {editingExistingField ? "Save field" : "Add field"}
                                    </Button>
                                </InlineStack>
                            </Box>
                        </Card>
                    </section>
                </div>
            ) : null}
        </>
    );
};

export default FitmentFieldsSettings;
